// MARK: NPM Modules
import React from "react";
import styled from "styled-components";
// MARK: Redux
import { RootState } from "reduxFunctionality/index";
import { useSelector } from "react-redux";
// MARK: Types
import ReduxStyleguide from "types/redux/styleguide";
import TextWrapType from "types/common/textWrap";

// MARK: Styled Components
interface StyledCSSTypographyProps {
  type?: string;
  display?: string;
  class: String;
  marginTop?: number;
  marginLeft?: number;
  margin?: string;
  color: string;
  textAlign?: string;
  textDecoration?: string;
  hoverTextDecoration?: string;
  flexdirection?: string;
  hoverColor?: string;
  pointer?: string;
  tableBorderBottom: string;
  minheight?: number;
  maxwidth?: number;
  textWrap?: string;
}

const Standard = styled.div<StyledCSSTypographyProps>`
  margin-block-start: 0em;
  margin-block-end: 0em;
  margin-inline-start: 0px;
  margin-inline-end: 0px;

  min-height: ${({ minheight }) => (minheight ? minheight + "px" : "")};
  max-width: ${({ maxwidth }) => (maxwidth ? maxwidth + "px" : "")};

  pointer: ${({ pointer }) => (pointer ? pointer : "")};
  display: ${({ display }) => (display ? display : "box")};
  flex-direction: ${({ flexdirection }) =>
    flexdirection ? flexdirection : "column"};

  margin-top: ${({ marginTop }) => (marginTop ? marginTop : 0)}px !important;
  margin-left: ${({ marginLeft }) => (marginLeft ? marginLeft : 0)}px;
  margin: ${({ margin }) => (margin ? margin : "")};

  color: ${({ color }) => color};
  font-style: normal;
  font-display: swap;

  li {
    list-style: circle;
  }

  text-align: ${({ textAlign }) => (textAlign ? textAlign : "left")};
  text-decoration: ${({ textDecoration }) =>
    textDecoration ? textDecoration : ""};

  :hover {
    color: ${({ hoverColor, color }) => (hoverColor ? hoverColor : color)};
    text-decoration: ${({ hoverTextDecoration }) =>
      hoverTextDecoration ? hoverTextDecoration : ""};
  }

  p:not(:last-child) {
    margin-top: 0px;
  }

  th,
  td,
  tr {
    width: 1%;
    vertical-align: top;
    border-bottom: ${({ tableBorderBottom }) =>
      "1px solid " + tableBorderBottom};
    padding-bottom: 10px;
    padding-top: 10px;
  }
  overflow-wrap: break-word;
  text-wrap: ${({ textWrap }) => (textWrap ? textWrap : "")};
`;

// MARK: React Component

// Props
interface TypographyProps {
  children: any;
  className: string;
  id?: string;
  type?: string;
  display?: string;
  margin?: string;
  marginTop?: number;
  marginLeft?: number;
  dangerouslySetInnerHTML?: boolean;
  color: string;
  textAlign?: string;
  textDecoration?: string;
  textWrap?: TextWrapType;
  flexdirection?: string;
  formatCopy?: boolean;
  hoverColor?: string;
  hoverTextDecoration?: string;
  pointer?: string;
  minheight?: number;
  maxwidth?: number;
  onClick?: () => void;
}

// Styleguide & Datapoints
const getDataPoints = (styleguide: ReduxStyleguide) => {
  const { colors } = styleguide;
  const { primary } = colors;
  return {
    tableBorderBottom: primary,
  };
};

// Component
const Typography = (props: TypographyProps) => {
  // MARK: React Redux Variables
  const styleguide: ReduxStyleguide = useSelector(
    (state: RootState) => state.styleguide
  );
  // MARK: Variables
  const { tableBorderBottom } = getDataPoints(styleguide);
  const {
    type,
    className,
    id,
    display,
    margin,
    marginTop,
    marginLeft,
    dangerouslySetInnerHTML,
    color,
    textAlign,
    textDecoration,
    textWrap,
    children,
    formatCopy,
    hoverColor,
    hoverTextDecoration,
    minheight,
    pointer,
    maxwidth,
    onClick,
  } = props;
  const headerType: any = type === null || type === undefined ? "div" : type;

  var textWrapString: string =
    type === null || type === undefined ? "div" : "balance";

  if (textWrap) {
    textWrapString = textWrap;
  }

  const setInnerHTML =
    dangerouslySetInnerHTML === null || dangerouslySetInnerHTML === undefined
      ? false
      : true;

  var formattedCopy = children;

  if (formatCopy) {
    formattedCopy = children
      .split("<p>")
      .join("")
      .split("</p>")
      .join("")
      .split("\r\n")
      .join("<br/>")
      .split("\n")
      .join("<br/>")
      .split("&amp;")
      .join("&");
  }

  switch (setInnerHTML) {
    case true:
      return (
        <Standard
          as={headerType}
          id={id}
          className={className}
          display={display}
          margin={margin}
          marginTop={marginTop}
          marginLeft={marginLeft}
          color={color}
          textAlign={textAlign}
          textDecoration={textDecoration}
          dangerouslySetInnerHTML={{ __html: formattedCopy }}
          hoverColor={hoverColor}
          hoverTextDecoration={hoverTextDecoration}
          pointer={pointer}
          tableBorderBottom={tableBorderBottom}
          minheight={minheight}
          maxwidth={maxwidth}
          textWrap={textWrapString}
          onClick={onClick}
        />
      );
    default:
      return (
        <Standard
          as={headerType}
          id={id}
          className={className}
          display={display}
          margin={margin}
          marginTop={marginTop}
          marginLeft={marginLeft}
          color={color}
          textAlign={textAlign}
          textDecoration={textDecoration}
          hoverColor={hoverColor}
          hoverTextDecoration={hoverTextDecoration}
          pointer={pointer}
          tableBorderBottom={tableBorderBottom}
          minheight={minheight}
          maxwidth={maxwidth}
          textWrap={textWrapString}
          onClick={onClick}
        >
          {formattedCopy}
        </Standard>
      );
  }
};

export default Typography;
